import { type JSX, useEffect, useState } from 'react';

import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_SUBMIT } from 'Config/Icons';
import { InviteThirdPartyContactsRequest, ThirdPartyContact, ThirdPartyService } from 'Models/TPRM';

import styles from './InviteContactsModal.module.css';

export interface InviteContactsModalProps {
    hideModal: () => void;
    service: ThirdPartyService;
    tprmAssessmentApi: TprmAssessmentApi;
}

interface FormState {
    isLoading: boolean;
    successMessage?: string;
    failureMessage?: string;
}

interface ContactWithRegistrationStatus {
    contact: ThirdPartyContact;
    hasRegistered: boolean;
}

/** Allows users to (re-)invite third-party contacts to complete due diligence questionnaires. */
export const InviteContactsModal = (props: InviteContactsModalProps): JSX.Element => {
    const [contactsWithRegistrationStatuses, setContactsWithRegistrationStatuses] = useState<ContactWithRegistrationStatus[] | undefined>(undefined);
    const [selectedContacts, setSelectedContacts] = useState<ThirdPartyContact[]>([]);
    const [formState, setFormState] = useState<FormState>({ isLoading: false });

    useEffect(() => {
        const loadContactsWithRegistrationStatus = async (): Promise<void> => {
            try {
                const validContacts = props.service.vendor_contacts.filter((contact) => contact.email_address !== undefined);

                // Get the list of emails for third-party users who have already successfully registered to complete questionnaires.
                const registeredEmails = (await props.tprmAssessmentApi.getEmailsRegisteredForAssessments(props.service.vendor_id, props.service.id)).data;
                const registeredEmailSet = new Set(registeredEmails);

                const _contactsWithRegistrationStatuses: ContactWithRegistrationStatus[] = validContacts.map((contact) => ({
                    contact,
                    hasRegistered: registeredEmailSet.has(contact.email_address!),
                }));
                setContactsWithRegistrationStatuses(_contactsWithRegistrationStatuses);

                // Preselect any contacts who haven't registered yet.
                setSelectedContacts(_contactsWithRegistrationStatuses.filter((c) => !c.hasRegistered).map((c) => c.contact));
            } catch (error) {
                setFormState({
                    isLoading: false,
                    failureMessage: error.message,
                });
            }
        };

        loadContactsWithRegistrationStatus();
    }, [props.service, props.tprmAssessmentApi]);

    const handleSubmit = async (): Promise<void> => {
        try {
            setFormState({ isLoading: true });

            const request: InviteThirdPartyContactsRequest = {
                contacts: selectedContacts,
            };
            await props.tprmAssessmentApi.inviteThirdPartyContactsToAssessments(props.service.vendor_id, props.service.id, request);

            setFormState({
                isLoading: false,
                successMessage: 'Contacts invited.',
            });
        } catch (error) {
            setFormState({
                isLoading: false,
                failureMessage: error.message,
            });
        }
    };

    const contactColumns: ColumnConfig<ContactWithRegistrationStatus>[] = [
        {
            type: ColumnType.CHECKBOX,
            name: 'Selected',
            getIsChecked: (row) => selectedContacts.some((c) => c.email_address === row.contact.email_address),
            getOnChecked: (row) => (isChecked) => {
                setSelectedContacts((current) => {
                    if (isChecked) {
                        return [...current, row.contact];
                    } else {
                        return current.filter((c) => c.email_address !== row.contact.email_address);
                    }
                });
            },
        },
        {
            type: ColumnType.STRING,
            name: 'Name',
            getString: (row) => row.contact.name,
        },
        {
            type: ColumnType.STRING,
            name: 'Email',
            getString: (row) => row.contact.email_address!,
        },
        {
            type: ColumnType.STRING,
            name: 'Has Registered',
            getString: (row) => (row.hasRegistered ? 'Yes' : 'No'),
        },
    ];

    if (contactsWithRegistrationStatuses === undefined) {
        return <Modal open isLoading closeModal={props.hideModal} header="Invite Third-Party Service Contacts" failureMessage={formState.failureMessage} />;
    }

    return (
        <Modal open header="Invite Third-Party Service Contacts">
            {formState.successMessage && <Alert type="success">{formState.successMessage}</Alert>}
            {formState.failureMessage && <Alert type="error">{formState.failureMessage}</Alert>}

            {contactsWithRegistrationStatuses.length === 0 ? (
                <>
                    <Text>There are no third-party service contacts with valid email addresses. Add contacts with email addresses to the service before inviting them.</Text>
                    <Text>If the third-party service contact(s) already have access to the due diligence questionnaires, this will delete their existing credentials and allow them to reregister.</Text>
                    <div className={styles.buttonContainer}>
                        <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                            Close
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <Text>Select the third-party contacts to invite to complete questionnaires. Each selected contact will receive an email with a link to access the third-party portal where they can respond to questionnaires. Reinviting a contact who has already registered will delete their existing credentials and allow them to reregister.</Text>
                    <Table<ContactWithRegistrationStatus> disableSorting columns={contactColumns} defaultSortColumn="Name" filters={[]} getUniqueIdForRow={(row) => row.contact.email_address!} rowsData={contactsWithRegistrationStatuses} />
                    <div className={styles.socialEngineeringWarning}>
                        <Text noStyles variant="Text3" color="red">
                            Due to the risk of social engineering, High Peaks Solutions strongly recommends confirming the identities of third-party service contacts using an out-of-band channel before inviting or reinviting them.
                        </Text>
                    </div>
                    <div className={styles.buttonContainer}>
                        <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                            Close
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} fontAwesomeImage={ICON_SUBMIT} isLoading={formState.isLoading} loadingText="Inviting..." disabled={selectedContacts.length === 0}>
                            Invite Selected Contacts
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};
